/* eslint-disable no-underscore-dangle, max-len */
import {
  isNull, each, orderBy, isNil, filter, includes, remove, get, some, isEmpty, pickBy,
} from 'lodash';
import {
  DATA_LOADING_STATES,
  PARTIAL_WIDGET_UNSUPPORTED_METRICS,
  METRIC_NAMES,
  SORT_DIRECTION,
  WIDGET_LOGICAL_NAMES,
  WIDGET_MODES,
  AGENT_STATUSES,
  FEATURES,
} from '@config/Consts';
import { ROOT_GETTER_PATHS, INFRA_GETTERS } from '@core/store';
import { ALERT_CONFIG } from '@config/AlertConfig';
import {
  getDialogAlertItemsByWidget,
  getLastTimeUpdated,
  getMetricLabel,
  timeframeMetricAlertData,
  sendThresholdEvent,
} from '@core/utils/utils';
import AlertModel from '@core/models/AlertModel';
import { AgentsAlertsService } from '@core/services/alerts/agents/AgentsAlertsService';
import { RESOURCES_METRICS_MAP, AGENT_LIST_RESOURCES } from './Consts';

const resourceMetricsUnsupported = {};

const _useWorkspacePartialWidgets = rootGetters =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_USE_PARTIAL_WIDGETS];

const _isAllResourceMetricsUnsupported = (rootGetters, resource) => {
  const unsupportedMetrics = get(PARTIAL_WIDGET_UNSUPPORTED_METRICS, WIDGET_LOGICAL_NAMES.AGENTS);
  if (isNil(resourceMetricsUnsupported[resource])) {
    let result = false;
    if (_useWorkspacePartialWidgets(rootGetters)) {
      const resourceMetrics = get(RESOURCES_METRICS_MAP, resource);
      if (resourceMetrics) {
        let allResourceMetricsUnsupported = true;
        each(resourceMetrics, (metric) => {
          allResourceMetricsUnsupported =
            allResourceMetricsUnsupported &&
            includes(unsupportedMetrics, metric);
        });
        result = allResourceMetricsUnsupported;
      }
    }
    resourceMetricsUnsupported[resource] = result;
  }

  return resourceMetricsUnsupported[resource];
};

const _getDataStatus = state => state.dataStatus;

const _getLastTimeDataUpdated = state => state.lastTimeDataUpdated;

const _isResourceOutOfDate = (state, resourceName) =>
  _getDataStatus(state)[resourceName] === DATA_LOADING_STATES.ERROR ||
  _getDataStatus(state)[resourceName] === DATA_LOADING_STATES.OBSOLETE;

const _getOutOfDateResources = (state) => {
  const obsoleteResources = [];
  each(Object.keys(_getDataStatus(state)), (resource) => {
    if (_isResourceOutOfDate(state, resource)) {
      obsoleteResources.push(resource);
    }
  });
  return obsoleteResources;
};

const _isOneOfTheResourceInSpecificStatus = ({ state, rootGetters }, status) => {
  let isOneOfTheResourceInSpecificStatus = false;
  each(Object.keys(_getDataStatus(state)), (resource) => {
    const allResourceMetricsUnsupported =
      _isAllResourceMetricsUnsupported(rootGetters, resource);
    isOneOfTheResourceInSpecificStatus =
      isOneOfTheResourceInSpecificStatus ||
      (!allResourceMetricsUnsupported && _getDataStatus(state)[resource] === status);
  });
  return isOneOfTheResourceInSpecificStatus;
};

const _isOneOfTheResourcesDown = (state, rootGetters) => _isOneOfTheResourceInSpecificStatus({ state, rootGetters }, DATA_LOADING_STATES.ERROR);

const _isOneOfTheResourcesObsolete = (state, rootGetters) => _isOneOfTheResourceInSpecificStatus({ state, rootGetters }, DATA_LOADING_STATES.OBSOLETE);

const getMetricAttributes = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_WIDGET_METRIC_ATTRIBUTES](WIDGET_LOGICAL_NAMES.AGENTS);

const getTimeFrameFilter = (state, getters, rootState, rootGetters) => rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_TIMEFRAME_FILTER];

const getUserIds = (state, getters, rootState, rootGetters) => rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_FILTERED_USERS];

const getUsers = (state, getters, rootState, rootGetters) => {
  const users = rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_ACCOUNT_USERS];
  return users;
};

const getAgentsInGroup = (state, getters) => {
  const accountUsers = getters.getUsers;
  const usersInGroupIds = getters.getUserIds;
  const agentsInGroup = filter(accountUsers, user => includes(usersInGroupIds, user.id));
  return agentsInGroup;
};

const getStatusReasons = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_ACCOUNT_STATUS_REASONS];

const getFilteredGroupsIds = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_GROUPS_FILTER];

const getSkillsFilter = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_FILTERED_SKILLS_IDS];

const hasAgentProfileOnly = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_HAS_AGENT_PROFILE_ONLY];

// const getAgentsMetrics = state => state.agentsMetrics || [];
const getAgentsMetrics = (state) => {
  if (state.sort.columnName === METRIC_NAMES.AGENT_VIEW_AGENT_NOTIFICATIONS) {
    AgentsAlertsService.sortAgentsByNotifications(state.agentsMetrics, state.sort);
  }
  return state.agentsMetrics || [];
};

const getWidgetMode = state => state.widgetMode;

const getStatusFilter = state => state.filter.status;

const getAgentsFilter = state => state.filter.agents;

const getTotalAgentStatuses = state => state.agentStatusSummary;

const getSort = state => state.sort;

const getGlobalFiltersUpdating = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_GLOBAL_FILTERS_UPDATING][WIDGET_LOGICAL_NAMES.AGENTS];

const dataHasBeenFetched = state => !isNull(state.agentsMetrics) && state.agentsMetrics.length > 0;

const isWaitingForData = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_ACCOUNT_DATA_STATUS] === DATA_LOADING_STATES.LOADING
  || _getDataStatus(state)[AGENT_LIST_RESOURCES.AGENT_VIEW] === DATA_LOADING_STATES.LOADING;

// In this widget we want to have an error just in case search agents data has already been fetched
// because otherwise we have a full widget error instead
const isDataObsolete = (state, getters, rootState, rootGetters) =>
  (_isOneOfTheResourcesObsolete(state, rootGetters) ||
    _isOneOfTheResourcesDown(state, rootGetters))
  && dataHasBeenFetched(state);

const isAgentStatusApiOutOfDate = state =>
  _isResourceOutOfDate(state, AGENT_LIST_RESOURCES.METRICS);

const agentsApiIsDown = (state, getters, rootState, rootGetters) =>
  (rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_ACCOUNT_DATA_STATUS] === DATA_LOADING_STATES.ERROR
    || _getDataStatus(state)[AGENT_LIST_RESOURCES.AGENT_VIEW] === DATA_LOADING_STATES.ERROR)
  && !dataHasBeenFetched(state);

const outOfDateMetrics = (state, getters, rootState, rootGetters) => {
  const resultMetrics = [];
  each(Object.keys(state.dataStatus), (resource) => {
    if (!_isAllResourceMetricsUnsupported(rootGetters, resource) &&
      _isResourceOutOfDate(state, resource)) {
      each(RESOURCES_METRICS_MAP[resource], (metricName) => {
        const metricLabel = getMetricLabel(WIDGET_LOGICAL_NAMES.AGENTS, metricName);
        if (!isNil(metricLabel)) {
          resultMetrics.push(metricLabel);
        }
      });
    }
  });
  return resultMetrics;
};

// As we have a different error message for Agent Status API (newly metrics api)
// we want to get the last time it was updated directly
const agentStatusApiLastTimeUpdated = (state) => {
  const lastTimeApiUpdated = _getLastTimeDataUpdated(state)[AGENT_LIST_RESOURCES.METRICS];
  return lastTimeApiUpdated ? getLastTimeUpdated(lastTimeApiUpdated) : null;
};

// In case we have couple of APIs failures
// we want to get the earliest time
const lastTimeDataUpdated = (state) => {
  const obsoleteResources = _getOutOfDateResources(state);
  remove(obsoleteResources, resource => resource === AGENT_LIST_RESOURCES.METRICS);
  const obsoleteResourcesTimeUpdated =
    filter(_getLastTimeDataUpdated(state), (value, key) =>
      !isNil(value) && includes(obsoleteResources, key));
  const orderedDataUpdateTimes = orderBy(obsoleteResourcesTimeUpdated, ['asc']);
  const earliestTimeDataWasUpdated = orderedDataUpdateTimes ? orderedDataUpdateTimes[0] : null;
  return earliestTimeDataWasUpdated ? getLastTimeUpdated(earliestTimeDataWasUpdated) : null;
};

const getPresetValues = (state, getters) => ({
  statusFilter: getters.getStatusFilter || [],
  agentsFilter: getters.getAgentsFilter || [],
  widgetMode: getters.getWidgetMode || WIDGET_MODES.CARDS,
  sort: getters.getSort || {
    columnName: METRIC_NAMES.AGENT_VIEW_AGENT_LOAD,
    direction: SORT_DIRECTION.DESCENDING,
  },
});

const getAlertData = (state, getters, rootState, rootGetters) => {
  const allAlerts = rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_ALERT_DATA];
  return get(allAlerts, WIDGET_LOGICAL_NAMES.AGENTS) || {};
};

const _getAlertDataByMetricName = (state, getters, metricName) => pickBy(getters.getAlertData, (value, key) => includes([metricName], key));

const _getThresholdEventOptions = (metricName, metricScore, alertData) => {
  const settings = {
    widgetName: WIDGET_LOGICAL_NAMES.AGENTS,
    metricName,
    metricScore,
    alertData,
  };
  return settings;
};

const _getMetricShowAlert = (state, getters, rootGetters, score, metricName) => {
  let alert = { showAlert: false, tooltip: '' };

  const alertPermissions = getters.getAlertPermissions;
  if (!alertPermissions.canViewAlert) {
    return alert;
  }
  if (isNaN(score)) {
    return alert;
  }
  if (getters.isAlertConfiguredForAgentStatus) {
    const metricAlertConfig = get(ALERT_CONFIG, `${WIDGET_LOGICAL_NAMES.AGENTS}.${metricName}`);
    const allMetricAlerts = _getAlertDataByMetricName(state, getters, METRIC_NAMES.AGENT_VIEW_AGENT_STATUS);
    const metricAlert = timeframeMetricAlertData(get(allMetricAlerts, metricName), score, metricAlertConfig.validation, metricAlertConfig.nameI18n);
    alert = metricAlert;
    if (metricAlert.showAlert) {
      sendThresholdEvent(_getThresholdEventOptions(metricName,
        score, get(getters.getAlertData, metricName)));
      alert.alertModel = new AlertModel({
        origin: `${WIDGET_LOGICAL_NAMES.AGENTS}_${metricName}`,
        message: alert.tooltip,
        showAlert: true });
    } else {
      alert.alertModel = new AlertModel({
        origin: `${WIDGET_LOGICAL_NAMES.AGENTS}_${metricName}`,
        message: alert.tooltip,
        showAlert: false });
    }
  }
  return alert;
};

const _getDialogAlertItemsByMetricName = (state, getters, metricName) =>
  getDialogAlertItemsByWidget([metricName], WIDGET_LOGICAL_NAMES.AGENTS, getters.getAlertData);

const showAlertConfigButtonForAgentStatus = (state, getters) => {
  const alertPermissions = getters.getAlertPermissions;
  const showAlertConfig = get(ALERT_CONFIG, `${WIDGET_LOGICAL_NAMES.AGENTS}.${METRIC_NAMES.AGENT_VIEW_AGENT_STATUS}.showAlertConfiguration`, false);
  return showAlertConfig && alertPermissions.canSetAlert;
};

const showAlertConfigButtonForStateDuration = (state, getters) => {
  const alertPermissions = getters.getAlertPermissions;
  const showAlertConfig = get(ALERT_CONFIG, `${WIDGET_LOGICAL_NAMES.AGENTS}.${METRIC_NAMES.AGENT_VIEW_AGENT_STATUS_WITH_REASON_DURATION}.showAlertConfiguration`, false);
  return showAlertConfig && alertPermissions.canSetAlert;
};
const showAlertConfigTransferRate = (state, getters, rootState, rootGetters) => {
  const alertPermissions = rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_HAS_ALERT_PERMISSIONS];
  const showAlertConfig = get(ALERT_CONFIG, `${WIDGET_LOGICAL_NAMES.AGENTS}.${METRIC_NAMES.AGENT_VIEW_TRANSFER_RATE}.showAlertConfiguration`, false);
  return showAlertConfig && alertPermissions.canSetAlert;
};

const showAlertConfigButtonForAgentLoad = (state, getters, rootState, rootGetters) => {
  const alertPermissions = rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_HAS_ALERT_PERMISSIONS];
  const showAlertConfig = get(ALERT_CONFIG, `${WIDGET_LOGICAL_NAMES.AGENTS}.${METRIC_NAMES.AGENT_VIEW_AGENT_LOAD}.showAlertConfiguration`, false);
  return showAlertConfig && alertPermissions.canSetAlert;
};

const isAlertConfiguredForAgentStatus = (state, getters) => {
  const agentStatusAlerts = _getAlertDataByMetricName(state, getters, METRIC_NAMES.AGENT_VIEW_AGENT_STATUS);
  let isAlertSet = false;
  some(agentStatusAlerts, (metricAlert) => {
    const values = get(metricAlert, 'values.timeframeValues');
    isAlertSet = some(values, value => !isEmpty(value.conditionType));
  });
  return isAlertSet;
};

const isAlertConfiguredForAgentStatusDuration = (state, getters) => {
  const statusMetricAlerts = _getAlertDataByMetricName(state, getters, METRIC_NAMES.AGENT_VIEW_AGENT_STATUS_WITH_REASON_DURATION);
  let isAlertSet = false;
  some(statusMetricAlerts, (metricAlert) => {
    const values = get(metricAlert, 'values.durationItems');
    isAlertSet = some(values, value => !isEmpty(value.conditionType));
  });
  return isAlertSet;
};
const isAlertConfiguredForTransferRate = (state, getters) => {
  const alerts = _getAlertDataByMetricName(state, getters, METRIC_NAMES.AGENT_VIEW_TRANSFER_RATE);
  return !isEmpty(alerts);
};

const showAlertConfigButtonForCSAT = (state, getters) => {
  const alertPermissions = getters.getAlertPermissions;
  const showAlertConfig = get(ALERT_CONFIG, `${WIDGET_LOGICAL_NAMES.AGENTS}.${METRIC_NAMES.AGENT_VIEW_CSAT}.showAlertConfiguration`, false);
  return showAlertConfig && alertPermissions.canSetAlert;
};

const isAlertConfiguredForCSAT = (state, getters) => {
  const alerts = _getAlertDataByMetricName(state, getters, METRIC_NAMES.AGENT_VIEW_CSAT);
  return !isEmpty(alerts);
};

const isAlertConfiguredForAgentLoad = (state, getters) => {
  const alerts = _getAlertDataByMetricName(state, getters, METRIC_NAMES.AGENT_VIEW_AGENT_LOAD);
  return !isEmpty(alerts);
};

const getDialogAlertItemsByMetricName = (state, getters) => (metricName) => {
  let alertItems;
  switch (metricName) {
    case METRIC_NAMES.AGENT_VIEW_AGENT_STATUS:
      alertItems = _getDialogAlertItemsByMetricName(state, getters, METRIC_NAMES.AGENT_VIEW_AGENT_STATUS);
      break;
    case METRIC_NAMES.AGENT_VIEW_AGENT_STATUS_WITH_REASON_DURATION:
      alertItems = _getDialogAlertItemsByMetricName(state, getters, METRIC_NAMES.AGENT_VIEW_AGENT_STATUS_WITH_REASON_DURATION);
      break;
    case METRIC_NAMES.AGENT_VIEW_CSAT:
      alertItems = _getDialogAlertItemsByMetricName(state, getters, METRIC_NAMES.AGENT_VIEW_CSAT);
      break;
    case METRIC_NAMES.AGENT_VIEW_AGENT_LOAD:
      alertItems = _getDialogAlertItemsByMetricName(state, getters, METRIC_NAMES.AGENT_VIEW_AGENT_LOAD);
      break;
    case METRIC_NAMES.AGENT_VIEW_TRANSFER_RATE:
      alertItems = _getDialogAlertItemsByMetricName(state, getters, METRIC_NAMES.AGENT_VIEW_TRANSFER_RATE);
      break;
    default:
      alertItems = [];
      break;
  }
  return alertItems;
};

const _getStateDurationMetricScore = () => 0;

const _getOnlineAgentsPercent = (state) => {
  const totalAgentStatuses = getTotalAgentStatuses(state);
  const onlineAgents = totalAgentStatuses[AGENT_STATUSES.ONLINE];
  const backSoonAgents = totalAgentStatuses[AGENT_STATUSES.BACK_SOON];
  const awayAgents = totalAgentStatuses[AGENT_STATUSES.AWAY];
  const percentOnlineAgents = (onlineAgents / (onlineAgents + backSoonAgents + awayAgents)) * 100;
  return percentOnlineAgents;
};

const getMetricScoreByMetricName = state => (metricName) => {
  let metricScore;
  switch (metricName) {
    case METRIC_NAMES.AGENT_VIEW_AGENT_STATUS:
      metricScore = _getOnlineAgentsPercent(state);
      break;
    case METRIC_NAMES.AGENT_VIEW_AGENT_STATUS_WITH_REASON_DURATION:
      metricScore = _getStateDurationMetricScore(state);
      break;
    default:
      metricScore = null;
      break;
  }
  return metricScore;
};

const showAlertWarningForAgentStatus = (state, getters, rootState, rootGetters) => {
  const percentOnlineAgents = _getOnlineAgentsPercent(state);
  const metricShowAlert = _getMetricShowAlert(state, getters, rootGetters,
    percentOnlineAgents, METRIC_NAMES.AGENT_VIEW_AGENT_STATUS);
  return metricShowAlert;
};

//
const getAlertPermissions = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_HAS_ALERT_PERMISSIONS];

const hasContactCenterFeature = (state, getters, rootState, rootGetters) =>
  rootGetters[INFRA_GETTERS.FEATURE_STATE](FEATURES.CONTACT_CENTER);

export default {
  getMetricAttributes,
  getTimeFrameFilter,
  getUserIds,
  getUsers,
  getAgentsInGroup,
  getAgentsMetrics,
  getStatusReasons,
  getFilteredGroupsIds,
  getWidgetMode,
  getStatusFilter,
  getSkillsFilter,
  getAgentsFilter,
  getTotalAgentStatuses,
  getSort,
  getGlobalFiltersUpdating,
  hasAgentProfileOnly,
  isDataObsolete,
  dataHasBeenFetched,
  isWaitingForData,
  agentsApiIsDown,
  outOfDateMetrics,
  lastTimeDataUpdated,
  isAgentStatusApiOutOfDate,
  agentStatusApiLastTimeUpdated,
  getPresetValues,
  getAlertData,
  showAlertConfigButtonForAgentStatus,
  isAlertConfiguredForAgentStatus,
  showAlertWarningForAgentStatus,
  getDialogAlertItemsByMetricName,
  getMetricScoreByMetricName,
  isAlertConfiguredForAgentStatusDuration,
  showAlertConfigButtonForStateDuration,
  isAlertConfiguredForCSAT,
  showAlertConfigButtonForCSAT,
  getAlertPermissions,
  showAlertConfigButtonForAgentLoad,
  isAlertConfiguredForAgentLoad,
  showAlertConfigTransferRate,
  isAlertConfiguredForTransferRate,
  hasContactCenterFeature,
};
